import { API_CONFIG, createApiUrl } from '../api'
import type { 
  UserProfileResponse, 
  UserProfileUpdateRequest
} from '../../types/types.user'

export class UserAPI {
  
  /**
   * Get user profile by user ID
   */
  static async getUserProfile(userId: string, accessToken: string): Promise<UserProfileResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.PROFILE.replace('{userId}', userId)
    )
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found')
      }
      if (response.status === 403) {
        throw new Error('Access denied')
      }
      throw new Error(`Failed to fetch user profile: ${response.statusText}`)
    }
    
    return response.json()
  }
  
  /**
   * Update user profile
   */
  static async updateProfile(
    userId: string, 
    profileData: UserProfileUpdateRequest,
    accessToken: string
  ): Promise<UserProfileResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.UPDATE_PROFILE.replace('{userId}', userId)
    )
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(profileData),
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update profile: ${error}`)
    }
    
    return response.json()
  }
  
  /**
   * Update user email
   */
  static async updateEmail(
    userId: string, 
    newEmail: string,
    accessToken: string
  ): Promise<UserProfileResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.UPDATE_EMAIL.replace('{userId}', userId)
    )
    
    const params = new URLSearchParams()
    params.append('newEmail', newEmail)
    
    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update email: ${error}`)
    }
    
    return response.json()
  }
  
  /**
   * Update user password
   */
  static async updatePassword(
    userId: string, 
    newPassword: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.UPDATE_PASSWORD.replace('{userId}', userId)
    )
    
    const params = new URLSearchParams()
    params.append('newPassword', newPassword)
    
    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update password: ${error}`)
    }
  }
}